import Papa from 'papaparse';
import {
  EditableAllocationRow,
  FlattenedGroupRow,
  GroupRow,
  TopLevelRow,
  isExploreRow,
  isFlattenedGroup
} from './post-optimization-constants';

interface ExportRow {
  networkContent: string;
  exploitExplore: string;
  format: string;
  placement: string;
  lastLive: string;
  allocationMetric: number | 'no data' | '';
  mustInclude: boolean | '';
  mustExclude: boolean | '';
  finalAllocation: number | '';
  cpm: string;
}

// Also fixes the CSV column order (Papa.unparse derives headers from the first row's keys)
const EMPTY_EXPORT_FIELDS: ExportRow = {
  networkContent: '',
  exploitExplore: '',
  format: '',
  placement: '',
  lastLive: '',
  allocationMetric: '',
  mustInclude: '',
  mustExclude: '',
  finalAllocation: '',
  cpm: ''
};

const groupFields = (row: GroupRow | FlattenedGroupRow): Partial<ExportRow> => ({
  networkContent: row.networkContent,
  exploitExplore: row.exploitExplore || '',
  format: row.format || '',
  cpm: row.aggregateCpm.toFixed(2)
});

const editableRowFields = (row: EditableAllocationRow): Partial<ExportRow> => ({
  placement: row.placement || '',
  lastLive: row.lastLive || '',
  allocationMetric: row.allocationMetric ?? '',
  mustInclude: row.mustInclude || false,
  mustExclude: row.mustExclude || false,
  finalAllocation: row.finalAllocation ?? 0
});

/** Build the flat, presentation-ready rows used by the CSV export. */
export const buildPostOptimizationExportRows = (data: TopLevelRow[]): ExportRow[] =>
  data.flatMap(row => {
    if (isFlattenedGroup(row)) {
      return {
        ...EMPTY_EXPORT_FIELDS,
        ...groupFields(row),
        ...editableRowFields(row)
      };
    }

    if (isExploreRow(row)) {
      return {
        ...EMPTY_EXPORT_FIELDS,
        ...groupFields(row),
        ...editableRowFields(row),
        finalAllocation: row.finalAllocation ?? row.budget
      };
    }

    return row.subRows.map(lineItem => ({
      ...EMPTY_EXPORT_FIELDS,
      networkContent: row.networkContent,
      exploitExplore: row.exploitExplore,
      format: row.format,
      cpm: lineItem.lineItemCpm.toFixed(2),
      ...editableRowFields(lineItem)
    }));
  });

/**
 * Export post-optimization data to CSV file
 * Flattens the hierarchical data and downloads the presentation-ready rows.
 */
export const exportPostOptimizationToCSV = (
  data: TopLevelRow[],
  company: string,
  runId: string
): void => {
  const csv = Papa.unparse(buildPostOptimizationExportRows(data));
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${company}-post-optimization-${runId}-${new Date()
    .toISOString()
    .slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};
